import jsPDF from 'jspdf'
import 'jspdf-autotable'

/**
 * Extract agreement text from various possible field names and formats
 */
function extractAgreementText(agreement) {
  // Try all possible field names
  let text = agreement.text || 
              agreement.content || 
              agreement.agreementContent ||
              agreement.agreementText ||
              agreement.body ||
              ''
  
  // If text is an array, join it
  if (Array.isArray(text)) {
    text = text.join('\n')
  }
  
  // If text is an object, try to extract text from it
  if (typeof text === 'object' && text !== null) {
    text = text.text || text.content || JSON.stringify(text)
  }
  
  // Ensure it's a string
  text = String(text || '').trim()
  
  return text
}

/**
 * Parse date from various Firestore formats
 */
function parseDate(dateValue) {
  if (!dateValue) return new Date()
  
  try {
    // Firestore Timestamp with toDate method
    if (typeof dateValue.toDate === 'function') {
      return dateValue.toDate()
    }
    // Already a Date object
    if (dateValue instanceof Date) {
      return dateValue
    }
    // Firestore Timestamp object {seconds, nanoseconds}
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000)
    }
    // String date
    if (typeof dateValue === 'string') {
      return new Date(dateValue)
    }
    // Number timestamp
    if (typeof dateValue === 'number') {
      return new Date(dateValue)
    }
  } catch (err) {
    console.warn('Error parsing date:', err)
  }
  
  return new Date()
}

export function generateAgreementPDF(agreement, signatures = {}) {
  if (!agreement) {
    throw new Error('Agreement data is required')
  }
  
  // Extract agreement text - try all possible sources
  const agreementText = extractAgreementText(agreement)
  
  // Debug logging
  console.log('PDF Generation:', {
    id: agreement.id,
    title: agreement.title,
    textLength: agreementText.length,
    hasClientSig: !!signatures.clientSignature,
    hasFreelancerSig: !!signatures.freelancerSignature
  })
  
  // Create new PDF document
  const doc = new jsPDF()
  
  // ============================================
  // HEADER SECTION
  // ============================================
  
  // Title
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  const title = agreement.title || 
                `Agreement with ${agreement.client || agreement.clientName || 'Client'}`
  doc.text(title, 20, 20)
  
  // Date and metadata
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  
  const createdDate = parseDate(agreement.createdAt)
  doc.text(`Date: ${createdDate.toLocaleDateString()}`, 20, 30)
  
  // Client name
  const clientName = agreement.client || agreement.clientName || ''
  if (clientName) {
    doc.text(`Client: ${clientName}`, 20, 37)
  }
  
  // Price
  const price = parseFloat(agreement.price || 0)
  if (price > 0) {
    const currency = agreement.currency || 'USD'
    doc.text(`Amount: ${currency} ${price.toLocaleString()}`, 20, clientName ? 44 : 37)
  }
  
  // Calculate content start position
  let contentStartY = 42
  if (price > 0) {
    contentStartY = clientName ? 55 : 48
  } else if (clientName) {
    contentStartY = 48
  }
  
  // ============================================
  // AGREEMENT CONTENT SECTION
  // ============================================
  
  doc.setFontSize(11)
  let currentY = contentStartY
  const pageHeight = 280
  const lineHeight = 6
  const margin = 20
  const contentWidth = 170
  
  if (!agreementText || agreementText.trim() === '') {
    // No content - show message
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(150, 150, 150)
    doc.text('This agreement does not contain any terms or content.', margin, currentY)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(0, 0, 0)
    currentY += 15
  } else {
    // Split text into lines that fit the page width
    const contentLines = doc.splitTextToSize(agreementText, contentWidth)
    
    // Render each line
    for (let i = 0; i < contentLines.length; i++) {
      // Check if we need a new page
      if (currentY > pageHeight - 80) {
        doc.addPage()
        currentY = 20
      }
      
      doc.text(contentLines[i], margin, currentY)
      currentY += lineHeight
    }
  }
  
  // ============================================
  // SIGNATURE SECTION
  // ============================================
  
  // Ensure signatures fit on page
  let signatureY = currentY + 30
  if (signatureY > pageHeight - 60) {
    doc.addPage()
    signatureY = 40
  }
  
  // Signature section header
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text('Signatures', margin, signatureY - 15)
  
  // Signature labels and lines
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(100, 100, 100)
  
  // Client signature (left side)
  doc.text('Client Signature', margin, signatureY - 5)
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, signatureY, margin + 75, signatureY)
  
  // Provider signature (right side)
  doc.text('Service Provider Signature', margin + 95, signatureY - 5)
  doc.line(margin + 95, signatureY, margin + 170, signatureY)
  
  // Add signature images
  doc.setTextColor(0, 0, 0)
  
  // Client signature image
  if (signatures.clientSignature?.signature) {
    try {
      doc.addImage(
        signatures.clientSignature.signature,
        'PNG',
        margin,
        signatureY - 28,
        70,
        22
      )
    } catch (error) {
      console.warn('Could not add client signature image:', error)
    }
  }
  
  // Freelancer signature image
  if (signatures.freelancerSignature?.signature) {
    try {
      doc.addImage(
        signatures.freelancerSignature.signature,
        'PNG',
        margin + 95,
        signatureY - 28,
        70,
        22
      )
    } catch (error) {
      console.warn('Could not add provider signature image:', error)
    }
  }
  
  // Signature dates and names
  doc.setFontSize(9)
  
  // Client signature info
  if (signatures.clientSignature?.timestamp) {
    doc.setTextColor(80, 80, 80)
    const clientSignDate = new Date(signatures.clientSignature.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    doc.text(`Date: ${clientSignDate}`, margin, signatureY + 8)
    if (clientName) {
      doc.text(`Name: ${clientName}`, margin, signatureY + 14)
    }
  } else {
    doc.setTextColor(150, 150, 150)
    doc.text('Date: _________________', margin, signatureY + 8)
    doc.text('Name: _________________', margin, signatureY + 14)
  }
  
  // Provider signature info
  if (signatures.freelancerSignature?.timestamp) {
    doc.setTextColor(80, 80, 80)
    const providerSignDate = new Date(signatures.freelancerSignature.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    doc.text(`Date: ${providerSignDate}`, margin + 95, signatureY + 8)
    const providerName = agreement.freelancerName || agreement.ownerName || ''
    if (providerName) {
      doc.text(`Name: ${providerName}`, margin + 95, signatureY + 14)
    }
  } else {
    doc.setTextColor(150, 150, 150)
    doc.text('Date: _________________', margin + 95, signatureY + 8)
    doc.text('Name: _________________', margin + 95, signatureY + 14)
  }
  
  // ============================================
  // FOOTER ON ALL PAGES
  // ============================================
  
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    
    // Left: Generated by
    doc.text('Generated by Klorah', margin, 285)
    
    // Center: Document ID
    const docIdText = `Document ID: ${agreement.id || 'Draft'}`
    const docIdWidth = doc.getTextWidth(docIdText)
    doc.text(docIdText, (210 - docIdWidth) / 2, 285)
    
    // Right: Page number
    const pageText = `Page ${i} of ${pageCount}`
    const pageWidth = doc.getTextWidth(pageText)
    doc.text(pageText, 210 - pageWidth - margin, 285)
  }
  
  return doc
}
